import React, { useEffect, useState } from "react";
import { Loader2, UserPlus, Ban, ShieldAlert } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ModulePage";
import StatusBadge from "@/components/StatusBadge";
import { useApp } from "@/context/AppContext";

const ROLES = ["PLATFORM_ADMIN", "ENTERPRISE_ADMIN", "AI_GOVERNANCE_ADMIN", "SECURITY_ADMIN", "AUDITOR", "AI_ENGINEER", "SOC_ANALYST", "BUSINESS_OWNER", "USE_CASE_OWNER", "VIEWER"];

export default function Users() {
  const { isAdmin } = useApp();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", name: "", role: "VIEWER", department: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/users").then(({ data }) => setUsers(data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try { await api.post("/users", form); setForm({ email: "", name: "", role: "VIEWER", department: "", password: "" }); load(); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const disable = async (id) => { await api.delete(`/users/${id}`); load(); };
  const changeRole = async (id, role) => { await api.put(`/users/${id}`, { role }); load(); };

  if (!isAdmin) return (
    <div><PageHeader title="User Manager" /><Card testId="no-access"><div className="flex items-center gap-3 py-8 text-muted-foreground"><ShieldAlert className="h-6 w-6 text-amber-400" /> Administrator access required. Your role does not permit user management.</div></Card></div>
  );

  return (
    <div>
      <PageHeader title="User Manager" subtitle="Create users, assign roles and manage access. Only administrators can manage users." readAloudContext={`User manager. ${users.length} users across roles. Administrators can create users, assign roles and disable accounts.`} />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold"><UserPlus className="h-4 w-4 text-accent" /> Create User</h3>
          {error && <div className="mb-2 rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>}
          <form onSubmit={create} className="space-y-2" data-testid="create-user-form">
            <input data-testid="cu-name" required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" />
            <input data-testid="cu-email" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" />
            <input data-testid="cu-dept" placeholder="Department" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" />
            <select data-testid="cu-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm">
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <input data-testid="cu-password" required type="password" placeholder="Temp password (min 8)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" />
            <button data-testid="cu-submit" disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2 font-semibold text-primary-foreground disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create User"}</button>
          </form>
        </Card>

        <Card className="lg:col-span-2">
          <h3 className="mb-3 text-sm font-semibold">Users</h3>
          {loading ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border text-xs uppercase text-muted-foreground">{["Name", "Email", "Role", "Status", ""].map((h) => <th key={h} className="px-2 py-2 text-start font-semibold">{h}</th>)}</tr></thead>
                <tbody>{users.map((u) => (
                  <tr key={u.id} data-testid={`user-${u.email}`} className="border-b border-border/50">
                    <td className="px-2 py-2.5 font-medium">{u.name}</td>
                    <td className="px-2 py-2.5 text-xs text-muted-foreground">{u.email}</td>
                    <td className="px-2 py-2.5">
                      <select value={u.role} onChange={(e) => changeRole(u.id, e.target.value)} disabled={u.role === "PLATFORM_ADMIN"} className="rounded-lg border border-border bg-background px-2 py-1 text-xs">
                        {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-2.5"><StatusBadge value={u.status === "ACTIVE" ? "Approved" : "FAIL"} /></td>
                    <td className="px-2 py-2.5">{u.role !== "PLATFORM_ADMIN" && u.status === "ACTIVE" && <button data-testid={`disable-${u.email}`} onClick={() => disable(u.id)} className="flex items-center gap-1 text-xs text-destructive hover:underline"><Ban className="h-3.5 w-3.5" /> Disable</button>}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
